import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// API root only — endpoint paths are appended below.
const API_BASE = 'https://uo0fpm33s8.execute-api.us-east-2.amazonaws.com/prod';
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const PLAN_DETAILS = {
  starter: {
    label: 'Starter',
    monthlyPrice: 79,
    price: '$79/month after trial',
    installPath: 'Self-serve install',
    description: 'For teams comfortable installing the widget themselves and using a lightweight monthly review to guide improvements.',
    features: [
      'Self-serve widget installation after preview',
      'Answers grounded in agreed product and support content',
      'Monthly lightweight performance snapshot based on available usage and question data',
      'One prioritized improvement recommendation each month',
      'Standard support for setup questions',
    ],
  },
  growth: {
    label: 'Growth',
    monthlyPrice: 299,
    price: '$299/month after trial',
    installPath: 'Done-for-you install assistance',
    description: 'For teams that want hands-on setup plus monthly implementation of improvements based on unanswered questions and content gaps.',
    features: [
      'Guided onboarding and content setup',
      'Widget installation assistance and QA',
      'Review of unanswered questions and content gaps',
      'One hands-on monthly improvement pass for answer quality and content gaps',
      'Support-workflow and escalation handoff review',
    ],
  },
};

function normalizeStoreUrl(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function getInitialPlan() {
  try {
    const params = new URLSearchParams(window.location.search);
    const plan = String(params.get('plan') || '').toLowerCase();
    if (plan === 'starter' || plan === 'growth') return plan;
  } catch {
    // Keep the default if URL parameters are unavailable.
  }
  return 'growth';
}

function getChannelLabel() {
  try {
    const params = new URLSearchParams(window.location.search);
    const channel = String(params.get('channel') || '').toLowerCase();
    if (channel === 'shopify') return 'Shopify store URL';
  } catch {
    // Use the generic label if URL parameters are unavailable.
  }
  return 'store or website URL';
}

function TrialForm() {
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [plan, setPlan] = useState(getInitialPlan);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // form | card | success
  const [error, setError] = useState('');
  const [trialData, setTrialData] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [copied, setCopied] = useState(false);

  const channelLabel = getChannelLabel();
  const selectedPlan = PLAN_DETAILS[plan] || PLAN_DETAILS.growth;
  const trialCreateUrl = useMemo(() => `${API_BASE}/api/trial/create`, []);
  const trialCompleteUrl = useMemo(() => `${API_BASE}/api/trial/complete`, []);

  async function startTrial(event) {
    event.preventDefault();
    setError('');

    if (!STRIPE_PUBLISHABLE_KEY) {
      setError('Stripe publishable key is missing.');
      return;
    }

    const normalizedStoreUrl = normalizeStoreUrl(storeUrl);
    if (!email.trim() || !normalizedStoreUrl) {
      setError('Email and store URL are required.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        trialCreateUrl,
        {
          email: email.trim(),
          storeUrl: normalizedStoreUrl,
          plan,
        },
        { timeout: 30000 }
      );

      const data = response?.data || {};
      if (!data.clientSecret || !data.trialId) {
        throw new Error('The server did not return the required trial details. Please try again.');
      }

      setTrialData(data);
      setClientSecret(data.clientSecret);
      setStep('card');
    } catch (requestError) {
      console.error('trial/create error:', requestError);
      setError(requestError?.response?.data?.error || requestError?.message || 'Failed to start trial. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function completeTrial() {
    setError('');

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please refresh and try again.');
      return;
    }
    if (!clientSecret) {
      setError('The card confirmation session is missing. Please go back and restart the trial.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card input is not ready yet. Please try again.');
      return;
    }

    setLoading(true);
    try {
      const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { email: email.trim() },
        },
      });

      if (confirmError) throw new Error(confirmError.message || 'Card confirmation failed.');
      if (!setupIntent?.id) throw new Error('Stripe did not return a setup intent. Please try again.');

      const response = await axios.post(
        trialCompleteUrl,
        {
          trialId: trialData.trialId,
          setupIntentId: setupIntent.id,
        },
        { timeout: 30000 }
      );

      setTrialData((previous) => ({ ...(previous || {}), ...(response?.data || {}) }));
      setStep('success');
    } catch (requestError) {
      console.error('trial/complete error:', requestError);
      setError(requestError?.response?.data?.error || requestError?.message || 'Failed to activate trial. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function copyTrialDetails() {
    const details = [
      'ScalableDeploy trial details',
      '',
      `Email: ${email}`,
      `Store: ${normalizeStoreUrl(storeUrl)}`,
      `Plan: ${selectedPlan.label} — ${selectedPlan.price}`,
      `Trial ID: ${trialData?.trialId || '—'}`,
      `Tenant ID: ${trialData?.tenantId || '—'}`,
    ].join('\n');

    try {
      if (!navigator.clipboard?.writeText) {
        setError('Clipboard access is unavailable in this browser. You can copy the details shown on this page.');
        return;
      }
      await navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (copyError) {
      console.warn('copy trial details failed', copyError);
      setError('Could not copy the details. You can still use the information shown on this page.');
    }
  }

  if (step === 'success') {
    const mailtoBody = [
      'Hi ScalableDeploy team,',
      '',
      'Here are the pages/content to prioritize for my preview:',
      '',
      '1.',
      '2.',
      '3.',
      '',
      'Priority questions customers ask:',
      '',
      'Notes or integration requirements:',
      '',
    ].join('\n');

    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center rounded-full border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
            Trial request received
          </div>
          <h1 className="mb-3 text-3xl font-bold">Your workspace is being prepared</h1>
          <p className="mx-auto mb-2 max-w-2xl text-gray-700">
            We received your request and will prepare a private ScalableDeploy preview using your store content.
          </p>
          <p className="mx-auto max-w-2xl text-sm text-gray-600">
            Setup is usually completed in about 48 hours. You will receive a separate message when your preview is ready after setup and QA.
          </p>
        </div>

        <div className="mb-8 grid gap-4 md:grid-cols-4">
          {[
            ['1. Setup', 'We prepare the workspace', 'We create the tenant, gather agreed source content, and configure the assistant.'],
            ['2. Preview', 'You get a private link', 'The preview is shared after setup and quality checks are complete.'],
            ['3. Install', 'Choose your path', 'Use the self-serve instructions or request done-for-you installation assistance.'],
            ['4. Snapshot', 'Review what was learned', 'Near the end of the trial, review observed questions, answer quality, gaps, and possible next steps.'],
          ].map(([eyebrow, title, description]) => (
            <div key={eyebrow} className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="mb-1 text-sm text-gray-500">{eyebrow}</div>
              <div className="font-semibold">{title}</div>
              <p className="mt-2 text-sm text-gray-600">{description}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h2 className="mb-2 text-lg font-semibold">Help us make the preview more useful</h2>
            <p className="mb-4 text-sm text-gray-600">
              We can start with your store URL. Sharing 3–10 priority pages, a product CSV, FAQs, policies, or customer questions helps focus the setup.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={`mailto:hello@scalabledeploy.com?subject=${encodeURIComponent('ScalableDeploy trial content')}&body=${encodeURIComponent(mailtoBody)}`}
                className="inline-flex justify-center rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              >
                Send priority content
              </a>
              <button type="button" onClick={copyTrialDetails} className="rounded border px-4 py-2 hover:bg-gray-50">
                {copied ? 'Copied trial details' : 'Copy trial details'}
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </div>

          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Trial details</h2>
            <div className="mb-4 space-y-2 text-sm text-gray-700">
              <div><strong>Email:</strong> {email || '—'}</div>
              <div><strong>Store:</strong> {normalizeStoreUrl(storeUrl) || '—'}</div>
              <div><strong>Plan:</strong> {selectedPlan.label} — {selectedPlan.price}</div>
              <div><strong>Install path:</strong> {selectedPlan.installPath}</div>
              <div><strong>Trial ID:</strong> {trialData?.trialId || '—'}</div>
              <div><strong>Tenant ID:</strong> {trialData?.tenantId || '—'}</div>
              <div><strong>Status:</strong> Setup in progress</div>
            </div>
            <div className="rounded border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
              Your preview link is shared after setup is complete, rather than sending you to an empty or unverified workspace.
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border bg-gray-50 p-5">
          <h2 className="mb-2 font-semibold">What the trial snapshot may cover</h2>
          <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
            <ul className="list-disc space-y-1 pl-5">
              <li>Questions asked and common intent patterns</li>
              <li>Product, policy, or support topics that came up</li>
              <li>Fallbacks, weak answers, and content gaps identified in review</li>
            </ul>
            <ul className="list-disc space-y-1 pl-5">
              <li>Answer-quality observations</li>
              <li>Practical support or product-discovery opportunities</li>
              <li>Recommended tuning priorities and next steps</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'card') {
    return (
      <div className="mx-auto max-w-xl p-6">
        <div className="mb-6">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-800">Card confirmation</div>
          <h1 className="mb-3 text-2xl font-bold">Confirm your card to activate the trial</h1>
          <p className="mb-3 text-gray-700">Your 7-day trial starts after card confirmation.</p>
          <p className="text-sm text-gray-600">The selected plan begins billing monthly when the trial ends unless you cancel beforehand.</p>
        </div>

        <div className="mb-4 rounded-xl border bg-white p-4 shadow-sm">
          <div className="mb-2 text-sm text-gray-500">Selected plan after trial</div>
          <div className="font-semibold">{selectedPlan.label} — {selectedPlan.price}</div>
          <p className="mt-1 text-sm text-gray-600">{selectedPlan.description}</p>
          <ul className="mt-3 space-y-1 text-sm text-gray-700">
            {selectedPlan.features.map((feature) => <li key={feature}>• {feature}</li>)}
          </ul>
        </div>

        <div className="mb-4 rounded-xl border bg-white p-4 shadow-sm">
          <CardElement options={{ hidePostalCode: true }} />
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
        <button
          type="button"
          onClick={completeTrial}
          disabled={loading || !stripe}
          className="w-full rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? 'Processing…' : 'Activate 7-day trial'}
        </button>
        <button
          type="button"
          onClick={() => { setError(''); setStep('form'); }}
          disabled={loading}
          className="mt-3 w-full rounded border px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
        >
          Back to details
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="grid items-start gap-8 lg:grid-cols-[1.05fr_0.95fr]">
        <section>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-800">
            <span className="font-semibold">7-day trial:</span> setup first · private preview · install choice · trial snapshot
          </div>
          <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">See your AI search + support experience before you install it.</h1>
          <p className="mb-5 text-lg text-gray-700">
            We prepare a private preview using agreed product and support content, so you can test real customer questions before installing the widget.
          </p>

          <div className="mb-6 grid gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-xl border bg-gray-50 p-4"><strong>Private preview first</strong><p className="mt-1 text-gray-600">Test real customer questions before installing anything on your store.</p></div>
            <div className="rounded-xl border bg-gray-50 p-4"><strong>Usually ready in ~48 hours</strong><p className="mt-1 text-gray-600">We curate content, configure the assistant, and check answers before sharing the preview.</p></div>
            <div className="rounded-xl border bg-gray-50 p-4"><strong>Choose your install path</strong><p className="mt-1 text-gray-600">Use the self-serve instructions or choose hands-on installation assistance.</p></div>
            <div className="rounded-xl border bg-gray-50 p-4"><strong>Trial snapshot</strong><p className="mt-1 text-gray-600">Review observed questions, answer-quality notes, and content gaps before deciding what to do next. After the trial, Starter includes a lightweight monthly snapshot and one prioritized recommendation.</p></div>
          </div>

          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h2 className="mb-2 font-semibold">What happens after you submit</h2>
            <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
              <li>Enter your work email and store URL.</li>
              <li>Confirm your card to activate the 7-day trial.</li>
              <li>We prepare your workspace and private preview.</li>
              <li>You test real questions and choose a self-serve or done-for-you install path.</li>
              <li>Near the end, review a trial snapshot and recommended next steps.</li>
            </ol>
          </div>
          <p className="mt-4 text-xs text-gray-500">Order lookup and human handoff depend on supported integrations and configuration; we confirm those workflows before describing them as live.</p>
        </section>

        <section className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="mb-3 text-2xl font-bold">Start the trial</h2>
          <p className="mb-5 text-sm text-gray-600">Enter your work email and {channelLabel}. We create your workspace first, then ask for card confirmation to activate the 7-day trial.</p>

          <form onSubmit={startTrial} className="space-y-4">
            <div>
              <label htmlFor="trial-email" className="block text-sm font-medium text-gray-700">Work email</label>
              <input id="trial-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className="mt-1 w-full rounded border px-3 py-2" placeholder="you@company.com" required />
            </div>

            <div>
              <label htmlFor="trial-store-url" className="block text-sm font-medium text-gray-700">Store or website URL</label>
              <input id="trial-store-url" type="text" autoComplete="url" value={storeUrl} onChange={(event) => setStoreUrl(event.target.value)} className="mt-1 w-full rounded border px-3 py-2" placeholder="yourstore.com" required />
            </div>

            <fieldset>
              <legend className="block text-sm font-medium text-gray-700">Plan after trial</legend>
              <div className="mt-2 grid gap-3">
                {(['growth', 'starter']).map((planKey) => {
                  const detail = PLAN_DETAILS[planKey];
                  const isSelected = plan === planKey;
                  return (
                    <label key={planKey} className={`block cursor-pointer rounded-xl border p-4 ${isSelected ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-gray-50'}`}>
                      <div className="flex items-start gap-3">
                        <input type="radio" name="plan" value={planKey} checked={isSelected} onChange={(event) => setPlan(event.target.value)} className="mt-1" />
                        <div className="min-w-0">
                          <div className="font-semibold">{detail.label} — ${detail.monthlyPrice}/month after trial</div>
                          <div className="mt-1 text-sm text-gray-600">{detail.description}</div>
                          <ul className="mt-2 space-y-1 text-sm text-gray-700">
                            {detail.features.map((feature) => <li key={feature}>• {feature}</li>)}
                          </ul>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="rounded border bg-gray-50 p-3 text-xs text-gray-600">
              A card is required to activate the trial. Your selected monthly plan starts when the 7-day trial ends unless you cancel beforehand. Pro is sales-assisted; <a className="text-indigo-600 underline" href="mailto:hello@scalabledeploy.com?subject=ScalableDeploy%20Pro%20inquiry">contact us about Pro</a> for larger or more customized requirements.
            </div>

            {error && <div role="alert" className="text-sm text-red-600">{error}</div>}
            <button type="submit" disabled={loading} className="w-full rounded-lg bg-indigo-600 px-4 py-3 font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Starting…' : 'Continue to card confirmation'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

export default function TrialSignupWrapper() {
  if (!stripePromise) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <div className="rounded border bg-yellow-50 p-4 text-yellow-800">
          Stripe publishable key is missing. Add REACT_APP_STRIPE_PUBLISHABLE_KEY and rebuild the frontend.
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <TrialForm />
    </Elements>
  );
}
